import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Target, Users, Search, Repeat, Shuffle } from 'lucide-react';
import apiService from '../services/apiService';
import CreateProjectWizard from './CreateProjectWizard';
import DashboardTicketList from './DashboardTicketList';
import { CustomerLine } from './CalendarTicketTitle';
import TicketDetail from './TicketDetail';
import '../styles/Dashboard.css';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import useIsMobile from '../hooks/useIsMobile';

const ProjectsView = () => {
  // W6 (#16): TicketDetail bekommt auf schmalen Screens das kompakte Mobil-Layout
  const isMobileScreen = useIsMobile();
  const { t, getLocale } = useI18n();
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [userEvents, setUserEvents] = useState({});
  const [userTickets, setUserTickets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const toggleBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 300 });
  // Auto-Archivierung: leere Vorgänge (keine aktiven Tickets) mit ausschließlich archivierten Zeitblöcken
  const autoArchivedProjectsRef = useRef(new Set()); // projectId set
  const autoArchivingProjectsRef = useRef(false);
  // New sections state
  const [projects, setProjects] = useState([]);
  const [subprojects, setSubprojects] = useState([]);
  const [projectsSearch, setProjectsSearch] = useState('');
  const [subprojectsSearch, setSubprojectsSearch] = useState('');
  const [ticketBlocks, setTicketBlocks] = useState([]);
  const [ticketBlocksSearch, setTicketBlocksSearch] = useState('');
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  // Prompt nach Erstellung: Direkt zur Zeitplanung?
  const [showPlanPrompt, setShowPlanPrompt] = useState(false);
  const [planPromptProject, setPlanPromptProject] = useState(null);
  // Hinweis: Der Vorgang-vollständig Prompt wird jetzt global im TicketDetail angezeigt
  // Behalte alle geplanten Tickets des Monats für projektbezogene Filter/Auto-Selection
  const [allPlannedTickets, setAllPlannedTickets] = useState([]);
  // Einmalige Vorbelegung aus URL-Parametern (z. B. ?projectId=.. oder ?subprojectId=..)
  const [initialSelectionApplied, setInitialSelectionApplied] = useState(false);
  // Templates für Badge am + Projekt Button
  const [templates, setTemplates] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const skipNextSelectionPersist = useRef(false);
  const shouldLoadHeatmap = useCallback((emails) => emails.length > 0 && !selectedProjectId, [selectedProjectId]);
  const loadHeatmapData = useCallback(async (emails, startDate, endDate, options = {}) => {
    const { isCancelled } = options;
    const isAborted = () => (typeof isCancelled === 'function' ? isCancelled() : false);

    if (!shouldLoadHeatmap(emails)) {
      if (!isAborted()) {
        setUserEvents({});
        setHeatmapLoading(false);
      }
      return;
    }

    setHeatmapLoading(true);
    try {
      const globalEventsResponse = await apiService.getGlobalEvents(emails, startDate, endDate);
      if (isAborted()) return;
      const globalUserEvents = globalEventsResponse.userEvents || {};
      setUserEvents(globalUserEvents);
    } catch (eventError) {
      if (isAborted()) return;
      console.error('Fehler beim Laden der Graph-Events für die Heatmap', eventError);
    } finally {
      if (!isAborted()) {
        setHeatmapLoading(false);
      }
    }
  }, [shouldLoadHeatmap]);

  // Aktuellen Benutzer laden (für Fallback-Selektion, wenn keine geplanten Blöcke existieren)
  useEffect(() => {
    const loadCurrent = async () => {
      try {
        const u = await apiService.getCurrentUser();
        setCurrentUser(u || null);
      } catch (_) {}
    };
    loadCurrent();
  }, []);

  const updateDropdownPosition = useCallback(() => {
    try {
      if (!toggleBtnRef.current) return;
      const rect = toggleBtnRef.current.getBoundingClientRect();
      const width = 300; // target panel width
      const left = Math.min(rect.right - width, window.innerWidth - width - 8) + window.scrollX;
      const top = rect.bottom + 6 + window.scrollY;
      setDropdownPos({ top, left, width });
    } catch (_) {}
  }, []);

  // Aus URL-Parametern Projekt vorauswählen
  useEffect(() => {
    if (initialSelectionApplied) return;
    try {
      const params = new URLSearchParams(window.location.search || '');
      const projectIdParam = params.get('projectId');
      const subprojectIdParam = params.get('subprojectId');
      const projectNameParam = params.get('projectName');
      const subprojectNameParam = params.get('subprojectName');
      if (!projectIdParam && !subprojectIdParam) return;

      // Wenn subprojectId angegeben ist, ermittle zugehöriges Projekt
      if (subprojectIdParam && (subprojects || []).length > 0) {
        const subIdNum = Number(subprojectIdParam);
        const sp = subprojects.find(s => Number(s.id) === subIdNum);
        if (sp && sp.project && sp.project.id != null) {
          setSelectedProjectId(sp.project.id);
          setInitialSelectionApplied(true);
          return;
        }
      }

      // Fallback: projectId direkt übernehmen, wenn vorhanden und Projekte geladen
      if (projectIdParam && (projects || []).length > 0) {
        const projIdNum = Number(projectIdParam);
        const exists = projects.some(p => Number(p.id) === projIdNum);
        if (exists) {
          setSelectedProjectId(projIdNum);
          setInitialSelectionApplied(true);
        }
      }

      // Name-basierte Auswahl
      if (!initialSelectionApplied) {
        if (projectNameParam && (projects || []).length > 0) {
          const p = projects.find(pr => String(pr.name).toLowerCase() === String(projectNameParam).toLowerCase());
          if (p) {
            setSelectedProjectId(p.id);
            setInitialSelectionApplied(true);
            return;
          }
        }
        if (subprojectNameParam && (subprojects || []).length > 0) {
          const sp = subprojects.find(s => String(s.name).toLowerCase() === String(subprojectNameParam).toLowerCase());
          if (sp && sp.project && sp.project.id != null) {
            setSelectedProjectId(sp.project.id);
            setInitialSelectionApplied(true);
            return;
          }
        }
      }
    } catch (_) {}
  }, [projects, subprojects, initialSelectionApplied]);

  // Load tickets for Ticket-Zeit-Blöcke (only those with project/subproject)
  useEffect(() => {
    const loadTickets = async () => {
      try {
        const res = await apiService.getTickets({ includeArchived: '1' });
        setTicketBlocks(res.tickets || []);
      } catch (e) {
        console.error('Fehler beim Laden der Tickets für Ticket-Zeit-Blöcke', e);
      }
    };
    loadTickets();
  }, []);

  const ticketBlocksFiltered = useMemo(() => {
    const byRelation = ticketBlocks.filter(t => t && (t.projectId || t.subprojectId));
    // Optional: filter by selected users if any selected
    const byAssignee = byRelation.filter(t => {
      if (!t.assignedTo) return false;
      if (selectedUsers.size === 0) return true;
      return selectedUsers.has(t.assignedTo);
    });
    // Zeige auch ungeplante Tickets; vorher waren nur geplante sichtbar
    return byAssignee;
  }, [ticketBlocks, selectedUsers]);

  useEffect(() => {
    if (!showUserDropdown) return;
    updateDropdownPosition();
    const onResize = () => updateDropdownPosition();
    const onScroll = () => updateDropdownPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showUserDropdown, updateDropdownPosition]);

  // Cookie helpers (same naming as GlobalCalendarView but scoped for projects)
  const setCookie = (name, value, days = 365) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = '; expires=' + date.toUTCString();
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  };
  const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Globale Ankündigung: Tickets wurden geändert
  const dispatchTicketsUpdated = (reason = '') => {
    try {
      window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason } }));
    } catch (_) {}
  };

  // Reagiere auf globale Ticket-Änderungen (z. B. aus GlobalCalendarView)
  useEffect(() => {
    const handleTicketsUpdated = async () => {
      try {
        // Liste der Zeitblöcke aktualisieren
        const ticketsRes = await apiService.getTickets({ includeArchived: '1' });
        const updatedTickets = ticketsRes.tickets || [];
        setTicketBlocks(updatedTickets);

        // Wenn ein Projekt ausgewählt ist: automatisch neu hinzugekommene Assignees hinzufügen,
        // damit deren Zeitblöcke nicht durch die User-Filter ausgeblendet werden
        if (selectedProjectId) {
          try {
            const relatedSubIds = new Set(
              (subprojects || []).filter(sp => sp.project && sp.project.id === selectedProjectId).map(sp => sp.id)
            );
            const relevant = updatedTickets.filter(t => (
              (t.projectId && t.projectId === selectedProjectId) ||
              (t.subprojectId && relatedSubIds.has(t.subprojectId))
            ));
            const emails = new Set();
            for (const t of relevant) if (t.assignedTo) emails.add(t.assignedTo);
            if (emails.size > 0) {
              setSelectedUsers(prev => {
                const next = new Set(prev);
                for (const e of emails) next.add(e);
                return next;
              });
            }
          } catch (_) {}
        }

        // Heatmap (geplante Tickets + Events) aktualisieren
        const selectedEmails = Array.from(selectedUsers);
        if (selectedEmails.length > 0) {
          // monthStart/monthEnd lokal berechnen, um TDZ-Probleme zu vermeiden
          const mStart = (() => { const d = new Date(currentMonth); d.setDate(1); d.setHours(0,0,0,0); return d; })();
          const mEnd = (() => { const d = new Date(currentMonth); d.setMonth(d.getMonth()+1); d.setDate(0); d.setHours(23,59,59,999); return d; })();
        const allTicketsResponse = await apiService.getPlannedTickets(mStart, mEnd);
        const allTickets = allTicketsResponse.tickets || [];
        setAllPlannedTickets(allTickets);
        const ticketsByUser = {};
        for (const email of selectedEmails) ticketsByUser[email] = [];
        for (const t of allTickets) {
          if (t.assignedTo && ticketsByUser[t.assignedTo]) {
            ticketsByUser[t.assignedTo].push(t);
          }
        }
        setUserTickets(ticketsByUser);

        // Lade Heatmap-Daten nun unabhängig vom restlichen Ladevorgang
        loadHeatmapData(selectedEmails, mStart, mEnd);
        }

                // Projekte/Unterprojekte/Vorlagen neu laden (für Prozentzahlen, Zähler und Badge)
                try {
                  const [projRes, subRes, templatesRes] = await Promise.all([
                    apiService.getProjects({ status: 'active' }),
                    apiService.getSubprojects({ status: 'active' }),
                    apiService.getTemplates()
                  ]);
                  setProjects(projRes.projects || []);
                  setSubprojects(subRes.subprojects || []);
                  setTemplates(templatesRes.templates || []);
                } catch (_) {}
      } catch (_) {}
    };
    window.addEventListener('ticketsUpdated', handleTicketsUpdated);
    return () => window.removeEventListener('ticketsUpdated', handleTicketsUpdated);
  }, [selectedUsers, currentMonth, selectedProjectId, subprojects, loadHeatmapData]);
  const loadSelectedUsersFromCookie = () => {
    const savedUsers = getCookie('projectsSelectedUsers');
    if (savedUsers) {
      try {
        const emailArray = JSON.parse(savedUsers);
        return new Set(emailArray);
      } catch (_) {
        return new Set();
      }
    }
    return null;
  };

  // Month boundaries
  const monthStart = useMemo(() => {
    const base = (currentMonth && !Number.isNaN(new Date(currentMonth).getTime())) ? new Date(currentMonth) : new Date();
    const d = new Date(base);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentMonth]);
  const monthEnd = useMemo(() => {
    const base = (currentMonth && !Number.isNaN(new Date(currentMonth).getTime())) ? new Date(currentMonth) : new Date();
    const d = new Date(base);
    d.setMonth(d.getMonth() + 1);
    d.setDate(0);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [currentMonth]);
  const daysInMonth = useMemo(() => monthEnd.getDate(), [monthEnd]);
  const dayEntries = useMemo(() => {
    const entries = [];
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(monthStart);
      d.setDate(i);
      const dow = d.getDay(); // 0=So, 6=Sa
      // Wochenenden als schmale Balken (Sa und So)
      entries.push({ day: d, dayNum: i, isSaturday: dow === 6 || dow === 0 });
    }
    return entries;
  }, [monthStart, daysInMonth]);
  const gridTemplateColumns = useMemo(() => {
    if (dayEntries.length === 0) return '';
    return dayEntries.map(e => (e.isSaturday ? '8px' : '1fr')).join(' ');
  }, [dayEntries]);

  // Abgeleitete Unterprojekt-IDs zum aktuell gewählten Projekt
  const selectedProjectRelatedSubIds = useMemo(() => {
    if (!selectedProjectId) return new Set();
    try {
      return new Set(
        (subprojects || [])
          .filter(sp => sp.project && sp.project.id === selectedProjectId)
          .map(sp => sp.id)
      );
    } catch (_) {
      return new Set();
    }
  }, [selectedProjectId, subprojects]);

  // Sichtbarkeit der Teilvorgänge-Sektion: nur zeigen, wenn ausgewähltes Projekt Teilvorgänge hat
  const hasSubprojectsForSelected = useMemo(() => {
    if (!selectedProjectId) return false;
    try {
      return (subprojects || []).some(sp => sp.project && sp.project.id === selectedProjectId);
    } catch (_) {
      return false;
    }
  }, [selectedProjectId, subprojects]);

  // Initial load users and selection
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const usersResponse = await apiService.getCalendarUsers();
        const allUsers = usersResponse.users || [];
        const activeUsers = allUsers.filter(user => {
          const isActive = user.isActive !== false;
          const hasEmail = user.email && user.email.trim() !== '';
          return isActive && hasEmail;
        });
        setUsers(activeUsers);

        // Initialize selection
        if (activeUsers.length > 0) {
          const savedSelection = loadSelectedUsersFromCookie();
          if (savedSelection !== null) {
            const availableEmails = new Set(activeUsers.map(u => u.email));
            const validSaved = new Set([...savedSelection].filter(email => availableEmails.has(email)));
            setSelectedUsers(validSaved);
          } else {
            setSelectedUsers(new Set(activeUsers.map(u => u.email)));
          }
        }
      } catch (e) {
        setError(t('projects.errorLoadUsers', 'Fehler beim Laden der Benutzer'));
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Persist selection (außer bei temporären Auto-Updates durch Projektwahl)
  useEffect(() => {
    if (users.length === 0) return;
    if (skipNextSelectionPersist.current) {
      skipNextSelectionPersist.current = false;
      return;
    }
    // nur persistieren, wenn Auswahl nicht gerade temporär überschrieben wurde
    const userEmails = Array.from(selectedUsers);
    setCookie('projectsSelectedUsers', JSON.stringify(userEmails));
  }, [selectedUsers, users.length]);

  // Load month data whenever selection or month changes
  useEffect(() => {
    const loadTickets = async () => {
      if (users.length === 0 || selectedUsers.size === 0) {
        setAllPlannedTickets([]);
        setUserTickets({});
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const selectedEmails = Array.from(selectedUsers);
        const allTicketsResponse = await apiService.getPlannedTickets(monthStart, monthEnd);

        const allTickets = allTicketsResponse.tickets || [];
        setAllPlannedTickets(allTickets);
        const ticketsByUser = {};
        for (const email of selectedEmails) ticketsByUser[email] = [];
        for (const t of allTickets) {
          if (t.assignedTo && ticketsByUser[t.assignedTo]) {
            ticketsByUser[t.assignedTo].push(t);
          }
        }
        setUserTickets(ticketsByUser);
      } catch (e) {
        setError('Fehler beim Laden der Monatsdaten');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadTickets();
  }, [users.length, selectedUsers, monthStart, monthEnd]);

  useEffect(() => {
    let cancelled = false;
    const selectedEmails = Array.from(selectedUsers);
    // Heatmap-Aufruf separat, damit UI schneller reagiert
    loadHeatmapData(selectedEmails, monthStart, monthEnd, { isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [selectedUsers, monthStart, monthEnd, loadHeatmapData]);

  // Load projects, subprojects and templates (active only)
  useEffect(() => {
    const load = async () => {
      try {
        const [projRes, subRes, templatesRes] = await Promise.all([
          apiService.getProjects({ status: 'active' }),
          apiService.getSubprojects({ status: 'active' }),
          apiService.getTemplates()
        ]);
        setProjects(projRes.projects || []);
        setSubprojects(subRes.subprojects || []);
        setTemplates(templatesRes.templates || []);
      } catch (e) {
        console.error('Fehler beim Laden von Projekten/Unterprojekten/Vorlagen', e);
      }
    };
    load();
  }, []);

  // Auto-Archive: Wenn ein Vorgang keinerlei aktive Zeitblöcke mehr hat, aber archivierte besitzt,
  // dann ist er faktisch abgeschlossen (z. B. wenn die Zeitblöcke per Script archiviert wurden)
  useEffect(() => {
    const run = async () => {
      try {
        if (autoArchivingProjectsRef.current) return;
        const list = Array.isArray(projects) ? projects : [];
        if (list.length === 0) return;

        const candidates = list.filter(p => {
          if (!p || p.id == null) return false;
          const idNum = Number(p.id);
          if (Number.isNaN(idNum)) return false;
          if (autoArchivedProjectsRef.current.has(idNum)) return false;
          // Nur Projekte ohne aktive Tickets, aber mit archivierten (historisch abgeschlossen)
          const activeCnt = Number(p.activeTicketCount || 0);
          const archivedCnt = Number(p.archivedTicketCount || 0);
          if (!(activeCnt === 0 && archivedCnt > 0)) return false;
          // Turnus-Projekte nicht automatisch archivieren (diese bleiben ggf. für nächste Periode bestehen)
          const hasTurnus = !!(p.turnusCount && p.turnusEvery && p.turnusUnit);
          if (hasTurnus) return false;
          return true;
        });

        if (candidates.length === 0) return;
        autoArchivingProjectsRef.current = true;

        // Archivieren (best-effort). Danach Listen neu laden.
        const results = await Promise.allSettled(
          candidates.map(p => apiService.archiveProjectIfEmpty(p.id))
        );
        candidates.forEach(p => {
          try { autoArchivedProjectsRef.current.add(Number(p.id)); } catch (_) {}
        });
        // Optional logging for debugging
        try {
          const ok = results.filter(r => r.status === 'fulfilled' && r.value && r.value.success === true).length;
          if (ok > 0) console.log(`🗄️ Auto-archived ${ok}/${candidates.length} empty projects`);
        } catch (_) {}

        const [projRes, subRes] = await Promise.all([
          apiService.getProjects({ status: 'active' }),
          apiService.getSubprojects({ status: 'active' })
        ]);
        const nextProjects = projRes.projects || [];
        setProjects(nextProjects);
        setSubprojects(subRes.subprojects || []);
        // Falls das aktuell selektierte Projekt archiviert wurde: Auswahl zurücksetzen
        if (selectedProjectId && !nextProjects.some(p => Number(p.id) === Number(selectedProjectId))) {
          setSelectedProjectId(null);
        }
      } catch (_) {
        // silent
      } finally {
        autoArchivingProjectsRef.current = false;
      }
    };
    run();
  }, [projects, selectedProjectId]);

  // User selection helpers
  const toggleUserSelection = (userEmail) => {
    const next = new Set(selectedUsers);
    if (next.has(userEmail)) next.delete(userEmail); else next.add(userEmail);
    setSelectedUsers(next);
  };
  const selectAllUsers = () => setSelectedUsers(new Set(users.map(u => u.email)));
  const deselectAllUsers = () => setSelectedUsers(new Set());

  const displayedUsers = users.filter(u => selectedUsers.has(u.email));

  // Fallback: Wenn Projekt ausgewählt ist, aber keine relevanten geplanten Tickets existieren,
  // zeige zumindest den aktuellen Benutzer (falls vorhanden), damit die Heatmap sichtbar bleibt.
  const hasAnyPlannedForSelectedProject = useMemo(() => {
    if (!selectedProjectId) return true; // keine Einschränkung nötig
    try {
      const relatedSubIds = selectedProjectRelatedSubIds;
      return (allPlannedTickets || []).some(t => (
        (t.projectId && t.projectId === selectedProjectId) ||
        (t.subprojectId && relatedSubIds.has(t.subprojectId))
      ));
    } catch (_) {
      return true;
    }
  }, [selectedProjectId, selectedProjectRelatedSubIds, allPlannedTickets]);

  const displayedUsersWithFallback = useMemo(() => {
    if (displayedUsers.length > 0) return displayedUsers;
    if (selectedProjectId && !hasAnyPlannedForSelectedProject && currentUser && currentUser.email) {
      const me = users.find(u => u.email === currentUser.email);
      return me ? [me] : [];
    }
    return displayedUsers;
  }, [displayedUsers, selectedProjectId, hasAnyPlannedForSelectedProject, currentUser, users]);

  // Calculate utilization hours for a day (events + tickets)
  const getUtilizationHoursForDay = (userEmail, dayDate) => {
    const events = userEvents[userEmail] || [];
    const tickets = userTickets[userEmail] || [];

    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayDate);
    dayEnd.setHours(23, 59, 59, 999);

    let totalMs = 0;

    // Events (Graph): nur berücksichtigen, wenn KEIN Projektfilter aktiv ist
    if (!selectedProjectId) {
      for (const ev of events) {
        try {
          const startUtcDateTime = ev.start?.dateTime?.endsWith('Z') ? ev.start.dateTime : ev.start?.dateTime ? ev.start.dateTime + 'Z' : null;
          const endUtcDateTime = ev.end?.dateTime?.endsWith('Z') ? ev.end.dateTime : ev.end?.dateTime ? ev.end.dateTime + 'Z' : null;
          if (!startUtcDateTime || !endUtcDateTime) continue;
          const s = new Date(startUtcDateTime);
          const e = new Date(endUtcDateTime);
          const overlapStart = s > dayStart ? s : dayStart;
          const overlapEnd = e < dayEnd ? e : dayEnd;
          if (overlapEnd > overlapStart) totalMs += overlapEnd - overlapStart;
        } catch (_) {}
      }
    }

    // Tickets (planned)
    const relevantTickets = selectedProjectId
      ? tickets.filter(t => (
          (t.projectId && t.projectId === selectedProjectId) ||
          (t.subprojectId && selectedProjectRelatedSubIds.has(t.subprojectId))
        ))
      : tickets;
    for (const t of relevantTickets) {
      try {
        const s = new Date(t.plannedStartDateTime);
        const e = new Date(t.plannedEndDateTime);
        const overlapStart = s > dayStart ? s : dayStart;
        const overlapEnd = e < dayEnd ? e : dayEnd;
        if (overlapEnd > overlapStart) totalMs += overlapEnd - overlapStart;
      } catch (_) {}
    }

    const hours = totalMs / (1000 * 60 * 60);
    return hours;
  };

  // Gesamt-Auslastung für einen Tag (Events + alle Tickets), unabhängig von Projektfilter
  const getOverallUtilizationHoursForDay = (userEmail, dayDate) => {
    const events = userEvents[userEmail] || [];
    const tickets = userTickets[userEmail] || [];

    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayDate);
    dayEnd.setHours(23, 59, 59, 999);

    let totalMs = 0;

    // Events (Graph)
    for (const ev of events) {
      try {
        const startUtcDateTime = ev.start?.dateTime?.endsWith('Z') ? ev.start.dateTime : ev.start?.dateTime ? ev.start.dateTime + 'Z' : null;
        const endUtcDateTime = ev.end?.dateTime?.endsWith('Z') ? ev.end.dateTime : ev.end?.dateTime ? ev.end.dateTime + 'Z' : null;
        if (!startUtcDateTime || !endUtcDateTime) continue;
        const s = new Date(startUtcDateTime);
        const e = new Date(endUtcDateTime);
        const overlapStart = s > dayStart ? s : dayStart;
        const overlapEnd = e < dayEnd ? e : dayEnd;
        if (overlapEnd > overlapStart) totalMs += overlapEnd - overlapStart;
      } catch (_) {}
    }

    // Alle geplanten Tickets (ohne Projektfilter)
    for (const t of tickets) {
      try {
        const s = new Date(t.plannedStartDateTime);
        const e = new Date(t.plannedEndDateTime);
        const overlapStart = s > dayStart ? s : dayStart;
        const overlapEnd = e < dayEnd ? e : dayEnd;
        if (overlapEnd > overlapStart) totalMs += overlapEnd - overlapStart;
      } catch (_) {}
    }

    const hours = totalMs / (1000 * 60 * 60);
    return hours;
  };

  // Nur Ticket-Stunden (alle Projekte), ohne Events – für globale Ansicht
  const getTicketHoursForDayAllProjects = (userEmail, dayDate) => {
    const tickets = userTickets[userEmail] || [];

    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayDate);
    dayEnd.setHours(23, 59, 59, 999);

    let totalMs = 0;
    for (const t of tickets) {
      try {
        const s = new Date(t.plannedStartDateTime);
        const e = new Date(t.plannedEndDateTime);
        const overlapStart = s > dayStart ? s : dayStart;
        const overlapEnd = e < dayEnd ? e : dayEnd;
        if (overlapEnd > overlapStart) totalMs += overlapEnd - overlapStart;
      } catch (_) {}
    }

    return totalMs / (1000 * 60 * 60);
  };

  // Wenn ein Projekt ausgewählt wird, automatisch alle Nutzer selektieren,
  // die Tickets (geplant oder ungeplant) für dieses Projekt/Unterprojekt haben,
  // und zum Start-Monat des ersten geplanten Tickets springen (falls vorhanden)
  useEffect(() => {
    if (!selectedProjectId) return;
    try {
      const relatedSubIds = selectedProjectRelatedSubIds;
      const availableEmails = new Set((users || []).map(u => u.email));
      const relevantTickets = (ticketBlocks || []).filter(t => {
        if (!t) return false;
        const matchesProject = (t.projectId && t.projectId === selectedProjectId) || (t.subprojectId && relatedSubIds.has(t.subprojectId));
        return matchesProject;
      });

      // Nutzer setzen
      const emails = new Set();
      for (const t of relevantTickets) {
        if (t.assignedTo && availableEmails.has(t.assignedTo)) {
          emails.add(t.assignedTo);
        }
      }
      if (emails.size > 0) {
        skipNextSelectionPersist.current = true;
        setSelectedUsers(new Set(emails));
      }

      // Zum frühesten Monat springen (nur WIRKLICH geplante Zeiten; ignoriere null/1970)
      let earliest = null;
      for (const t of relevantTickets) {
        try {
          const psd = t && t.plannedStartDateTime;
          if (!psd) continue; // ungeplant -> überspringen
          const s = new Date(psd);
          const time = s.getTime();
          if (!Number.isNaN(time) && time > Date.UTC(2000, 0, 1)) {
            if (!earliest || time < earliest.getTime()) earliest = s;
          }
        } catch (_) {}
      }
      if (earliest && !Number.isNaN(earliest.getTime()) && earliest.getTime() > Date.UTC(2000, 0, 1)) {
        const d = new Date(earliest);
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        setCurrentMonth(d);
      } else {
        // Kein geplanter Zeitblock vorhanden -> auf aktuellen Monat springen
        const nowMonth = new Date();
        nowMonth.setDate(1);
        nowMonth.setHours(0, 0, 0, 0);
        setCurrentMonth(nowMonth);
      }
    } catch (_) {}
  }, [selectedProjectId, selectedProjectRelatedSubIds, users, ticketBlocks]);

  // Map 0..8h to color between white and dark red; >=8 -> dark red
  const getDayColor = (hours) => {
    if (hours <= 0.01) return 'var(--color-heat-leer)';
    const clamped = Math.min(hours, 8);
    const t = clamped / 8; // 0..1
    const from = { r: 255, g: 255, b: 255 };
    const to = { r: 220, g: 53, b: 69 }; // max red per spec (Bootstrap danger)
    const r = Math.round(from.r + (to.r - from.r) * t);
    const g = Math.round(from.g + (to.g - from.g) * t);
    const b = Math.round(from.b + (to.b - from.b) * t);
    return `rgb(${r},${g},${b})`;
  };

  // Gelbe Skala für Nicht-Projekt-Tage
  const getDayColorYellow = (hours) => {
    if (hours <= 0.01) return 'var(--color-heat-leer)';
    const clamped = Math.min(hours, 8);
    const t = clamped / 8;
    const from = { r: 255, g: 255, b: 255 };
    const to = { r: 255, g: 193, b: 7 }; // amber
    const r = Math.round(from.r + (to.r - from.r) * t);
    const g = Math.round(from.g + (to.g - from.g) * t);
    const b = Math.round(from.b + (to.b - from.b) * t);
    return `rgb(${r},${g},${b})`;
  };

  const navigateMonth = (delta) => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + delta);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    setCurrentMonth(d);
  };
  const goToThisMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    setCurrentMonth(d);
  };

  const monthLabel = useMemo(() => new Intl.DateTimeFormat(getLocale(), { month: 'long', year: 'numeric' }).format(monthStart), [monthStart, getLocale]);

  // Badge-Zähler: Anzahl der Due-Templates
  const dueTemplatesCount = useMemo(() => {
    return (templates || []).filter(tpl => !!tpl._dueThisPeriod).length;
  }, [templates]);

  // Reflect inline edits immediately in der Zeitblöcke-Liste
  const handleTicketUpdated = useCallback((updatedTicket) => {
    if (!updatedTicket || !updatedTicket.id) return;
    setTicketBlocks(prev => prev.map(t => t.id === updatedTicket.id ? { ...t, ...updatedTicket } : t));
    setSelectedTicket(prev => (prev && prev.id === updatedTicket.id) ? { ...prev, ...updatedTicket } : prev);
  }, []);

  return (
    <div className="container">
      <div className="dashboard-section projects-view" style={{ width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="projects-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 30 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Calendar size={22} />
          <h2 style={{ margin: 0, textTransform: 'capitalize' }}>{monthLabel}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => navigateMonth(-1)} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ChevronLeft size={16} />
            {t('projects.prevMonth', 'Vorheriger Monat')}
          </button>
          <button onClick={goToThisMonth} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Target size={16} />
            {t('projects.thisMonth', 'Dieser Monat')}
          </button>
          <button onClick={() => navigateMonth(1)} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {t('projects.nextMonth', 'Nächster Monat')}
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="user-dropdown-container" style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title={t('projects.selectUsers', 'Benutzer auswählen')}
            ref={toggleBtnRef}
          >
            <Users size={16} />
            {selectedUsers.size} {t('projects.users', 'Benutzer')}
          </button>
          {showUserDropdown && (
            <div
              style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, background: 'white', color: 'var(--color-text-strong-alt)', borderRadius: 6, boxShadow: '0 6px 18px var(--color-shadow-20)', padding: 10, minWidth: dropdownPos.width, maxHeight: 320, overflowY: 'auto', zIndex: 9999 }}
              onMouseLeave={() => setShowUserDropdown(false)}
            >
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button className="btn btn-secondary" onClick={selectAllUsers}>{t('projects.selectAll', 'Alle auswählen')}</button>
                <button className="btn btn-secondary" onClick={deselectAllUsers}>{t('projects.deselectAll', 'Alle abwählen')}</button>
              </div>
              {users.map(u => (
                <label key={u.email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedUsers.has(u.email)} onChange={() => toggleUserSelection(u.email)} />
                  <span style={{ fontSize: 14 }}>{u.displayName || u.email}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {heatmapLoading && (
          // Zeige separaten Status, während die Heatmap-Daten (Graph) nachladen
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-light)', fontSize: 13 }}>
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            {t('projects.heatmapLoading', 'Heatmap wird aktualisiert...')}
          </div>
        )}
        </div>

        {error && (
        <div className="alert alert-error" style={{ marginBottom: 8 }}>{error}</div>
        )}

        <div style={{ flex: '1 1 auto', overflowX: 'hidden', overflowY: 'auto', paddingBottom: 8 }}>
          {loading && users.length === 0 ? (
            <div style={{ padding: 12 }}>Lade Daten...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {displayedUsersWithFallback.map(user => (
                <div key={user.email} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{user.displayName || user.email}</div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: gridTemplateColumns,
                      columnGap: 2,
                      width: '100%'
                    }}
                  >
                    {dayEntries.map(({ day, dayNum, isSaturday }) => {
                      if (isSaturday) {
                        const isToday = (() => {
                          const t = new Date();
                          return t.getFullYear() === day.getFullYear() && t.getMonth() === day.getMonth() && t.getDate() === day.getDate();
                        })();
                        return (
                          <div key={dayNum} title={t('projects.weekend', 'Wochenende')} style={{ height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <div style={{ width: '100%', height: '100%', backgroundColor: 'var(--color-surface-black)', borderRadius: 2 }} />
                            {isToday && (
                              <>
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: '50%',
                                    width: 2,
                                    backgroundColor: 'var(--color-danger)',
                                    transform: 'translateX(-1px)',
                                    zIndex: 2,
                                    boxShadow: '0 1px 3px var(--color-danger-shadow-30)'
                                  }}
                                />
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: -4,
                                    width: 10,
                                    height: 10,
                                    backgroundColor: 'var(--color-danger)',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    transform: 'translateX(-50%)',
                                    zIndex: 3,
                                    boxShadow: '0 2px 4px var(--color-shadow-20)'
                                  }}
                                />
                              </>
                            )}
                          </div>
                        );
                      }
                      const projectHours = getUtilizationHoursForDay(user.email, day);
                      let hoursForTooltip = projectHours;
                      let color;
                      if (selectedProjectId) {
                        // Projektfilter aktiv: Rot = Stunden im ausgewählten Projekt, Gelb = andere belegte Stunden, sonst Weiß
                        if (projectHours > 0.01) {
                          color = getDayColor(projectHours);
                        } else {
                          const overallHours = getOverallUtilizationHoursForDay(user.email, day);
                          if (overallHours > 0.01) {
                            color = getDayColorYellow(overallHours);
                            hoursForTooltip = overallHours;
                          } else {
                            color = 'var(--color-heat-leer)';
                            hoursForTooltip = 0;
                          }
                        }
                      } else {
                        // Kein Projektfilter: Rot = alle Ticket-Stunden (projektübergreifend), Gelb = Event-Stunden ohne Tickets, sonst Weiß
                        const ticketHours = getTicketHoursForDayAllProjects(user.email, day);
                        if (ticketHours > 0.01) {
                          color = getDayColor(ticketHours);
                          hoursForTooltip = ticketHours;
                        } else {
                          const overallHours = getOverallUtilizationHoursForDay(user.email, day);
                          if (overallHours > 0.01) {
                            color = getDayColorYellow(overallHours);
                            hoursForTooltip = overallHours;
                          } else {
                            color = 'var(--color-heat-leer)';
                            hoursForTooltip = 0;
                          }
                        }
                      }
                      const isToday = (() => {
                        const t = new Date();
                        return t.getFullYear() === day.getFullYear() && t.getMonth() === day.getMonth() && t.getDate() === day.getDate();
                      })();
                      return (
                        <div
                          key={dayNum}
                          title={t('projects.dayHoursTooltip', `Tag ${dayNum}: ${hoursForTooltip.toFixed(2)} Std.`, { day: dayNum, hours: hoursForTooltip.toFixed(2) })}
                          style={{
                            height: 18,
                            backgroundColor: color,
                            border: '1px solid var(--color-border-translucent)',
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'var(--text-micro)',
                            color: 'var(--color-text-strong-alt)',
                            userSelect: 'none',
                            position: 'relative'
                          }}
                        >
                          {dayNum}
                          {isToday && (
                            <>
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  bottom: 0,
                                  left: '50%',
                                  width: 2,
                                  backgroundColor: 'var(--color-danger)',
                                  transform: 'translateX(-1px)',
                                  zIndex: 2,
                                  boxShadow: '0 1px 3px var(--color-danger-shadow-30)'
                                }}
                              />
                              <div
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  top: -4,
                                  width: 10,
                                  height: 10,
                                  backgroundColor: 'var(--color-danger)',
                                  borderRadius: '50%',
                                  border: '2px solid white',
                                  transform: 'translateX(-50%)',
                                  zIndex: 3,
                                  boxShadow: '0 2px 4px var(--color-shadow-20)'
                                }}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Horizontal sections under calendar */}
      <div style={{ marginTop: 20 }}>
        <div className="dashboard-grid">
          <div className="dashboard-section">
            <div className="section-header section-header-with-search">
              <Shuffle size={20} />
              <h2>{t('header.projects', 'Vorgänge')}</h2>
              <div className="ticket-search-container">
                <div className="search-input-wrapper">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    placeholder={t('projects.searchProjects', 'Vorgänge durchsuchen...')}
                    value={projectsSearch}
                    onChange={(e) => setProjectsSearch(e.target.value)}
                    className="ticket-search-input"
                  />
                </div>
              </div>
              <button 
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', position: 'relative' }}
                onClick={() => setShowCreateProjectModal(true)}
                title={t('projects.newProject', 'Neuen Vorgang anlegen')}
              >
                {t('projects.addProjectShort', '+ Vorgang')}
                {dueTemplatesCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'var(--color-danger)',
                      color: 'white',
                      borderRadius: '50%',
                      minWidth: 18,
                      height: 18,
                      fontSize: 12,
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      boxShadow: '0 2px 4px var(--color-shadow-20)'
                    }}
                    title={t('projects.dueTemplatesCount', '{{count}} Bibliothekseinträge fällig', { count: dueTemplatesCount })}
                  >
                    {dueTemplatesCount > 99 ? '99+' : dueTemplatesCount}
                  </span>
                )}
              </button>
            </div>
            <div className="dashboard-ticket-list" style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
              {projects
                .filter(p => !projectsSearch || (p.name?.toLowerCase().includes(projectsSearch.toLowerCase()) || p.projectNumber?.toLowerCase().includes(projectsSearch.toLowerCase())))
                .sort((a, b) => {
                  // Hilfsfunktion: Prüfe ob ungeplante Tickets existieren
                  const hasUnplannedTickets = (proj) => {
                    const relatedSubIds = new Set(
                      subprojects.filter(sp => sp.project && sp.project.id === proj.id).map(sp => sp.id)
                    );
                    return ticketBlocks.some(t =>
                      !t?.isArchived && t?.status !== 'abgeschlossen' &&
                      (
                        ((t.projectId && t.projectId === proj.id) || (t.subprojectId && relatedSubIds.has(t.subprojectId))) &&
                        (!t.isPlanned || !t.plannedStartDateTime)
                      )
                    );
                  };
                  
                  // Neue Dimmed-Logik: Turnus erfüllt UND alle Tickets eingeplant
                  const aDimmed = !!a._dimmed && !hasUnplannedTickets(a);
                  const bDimmed = !!b._dimmed && !hasUnplannedTickets(b);
                  
                  // Due: Turnus fällig ODER ungeplante Tickets vorhanden
                  const aDue = !!a._dueThisPeriod || hasUnplannedTickets(a);
                  const bDue = !!b._dueThisPeriod || hasUnplannedTickets(b);
                  
                  // Sortierung: Due zuerst, dann Dimmed nach unten
                  if (aDue && !bDue) return -1;
                  if (!aDue && bDue) return 1;
                  if (aDimmed && !bDimmed) return 1;
                  if (!aDimmed && bDimmed) return -1;
                  return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                })
                .map(p => {
                  // Berechne Dimmed-Status für diese Karte
                  const relatedSubIds = new Set(
                    subprojects.filter(sp => sp.project && sp.project.id === p.id).map(sp => sp.id)
                  );
                  const hasUnplanned = ticketBlocks.some(t =>
                    !t?.isArchived && t?.status !== 'abgeschlossen' &&
                    (
                      ((t.projectId && t.projectId === p.id) || (t.subprojectId && relatedSubIds.has(t.subprojectId))) &&
                      (!t.isPlanned || !t.plannedStartDateTime)
                    )
                  );
                  const isDimmed = !!p._dimmed && !hasUnplanned;
                  const planningRequired = (p._dueThisPeriod || hasUnplanned);
                  // Turnus-Icon anhand verknüpfter Vorlage (direkt am Projekt oder via templateId aus globalen Templates)
                  const projectTemplate = p.template || (p.templateId != null ? (templates || []).find(tpl => Number(tpl.id) === Number(p.templateId)) : null);
                  const hasTurnus = !!(projectTemplate && projectTemplate.turnusCount && projectTemplate.turnusEvery && projectTemplate.turnusUnit);
                  
                  return (
                  <div
                    key={p.id}
                    className="dashboard-ticket-item"
                    style={{ minWidth: 320, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6, border: selectedProjectId === p.id ? '2px solid var(--color-primary)' : undefined, opacity: isDimmed ? 0.6 : 1 }}
                    onClick={() => { setSelectedProjectId(p.id); }}
                    title={t('projects.selectProject', 'Projekt auswählen')}
                  >
                    {/* #103: Kunde fett über dem Vorgangsnamen */}
                    <CustomerLine customerId={p.customerId} className="dashboard-ticket-title" />
                    <div className="dashboard-ticket-title" style={{ display: 'flex', justifyContent: 'space-between', fontWeight: p.customerId ? 'normal' : undefined }}>
                      <span title={p.name} style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
                      <span style={{ opacity: 0.7, marginLeft: 8, whiteSpace: 'nowrap' }}>{p.projectNumber}</span>
                    </div>
                    <div className="dashboard-ticket-meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="ticket-status-info">
                        <span className="ticket-status-text">{t('projects.completed', 'Abgeschlossen')} {p.completedPercent || 0}%</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-light)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {hasTurnus && (
                          <Repeat size={14} title={t('projects.turnusConfigured', 'Turnus konfiguriert')} />
                        )}
                        <span>{(() => { const subCount = subprojects.filter(sp => sp.project && sp.project.id === p.id).length; return subCount > 0 ? `${subCount} ${t('projects.subprojects', 'Teilvorgänge')}` : `${p.ticketCount || 0} ${t('projects.timeblocks', 'Zeitblöcke')}`; })()}</span>
                      </div>
                    </div>
                    {hasTurnus && (
                      <div
                        style={{ fontSize: 12, color: 'var(--color-text-light)' }}
                        title={p.lastPlannedActionAt
                          ? `${t('projects.lastPlannedTooltip', 'eingeplant am')} ${new Date(p.lastPlannedActionAt).toLocaleDateString()}${p.lastPlannedBy ? ` ${t('projects.lastPlannedBy', 'von')} ${p.lastPlannedBy}` : ''}`
                          : undefined}
                      >
                        {p.lastPlannedAt
                          ? `${t('projects.lastPlannedAt', 'Zuletzt eingeplant')}: ${new Date(p.lastPlannedAt).toLocaleDateString()}`
                          : t('projects.neverPlanned', 'Noch nie eingeplant')}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 8, width: '100%', maxWidth: '100%' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                        setShowProjectDetail({ mode: 'project', data: p });
                        }}
                        style={{ width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {t('projects.edit', 'Bearbeiten')}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          const url = `/global-calendar?projectPlanId=${p.id}&projectName=${encodeURIComponent(p.name || '')}`;
                          navigate(url);
                        }}
                        style={{ position: 'relative', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', border: planningRequired ? '3px solid var(--color-danger)' : '3px solid transparent' }}
                      >
                        {planningRequired ? (
                          <span style={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, borderTop: '12px solid var(--color-danger)', borderLeft: '12px solid transparent', borderTopRightRadius: 4 }} />
                        ) : null}
                        {planningRequired
                          ? t('projects.planningNeeded', 'Zeitplanung nötig')
                          : t('projects.planning', 'Zeitplanung')}
                      </button>
                    </div>
                  </div>
                  );
                })}
            </div>
          </div>

          {hasSubprojectsForSelected && (
            <div className="dashboard-section">
              <div className="section-header section-header-with-search">
                <Shuffle size={20} />
                <h2>{t('projects.subprojects', 'Teilvorgänge')}</h2>
                <div className="ticket-search-container">
                  <div className="search-input-wrapper">
                    <Search size={16} className="search-icon" />
                    <input
                      type="text"
                      placeholder={t('projects.searchSubprojects', 'Teilvorgänge durchsuchen...')}
                      value={subprojectsSearch}
                      onChange={(e) => setSubprojectsSearch(e.target.value)}
                      className="ticket-search-input"
                    />
                  </div>
                </div>
              </div>
              <div className="dashboard-ticket-list" style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                {(selectedProjectId ? subprojects.filter(sp => (sp.project && sp.project.id === selectedProjectId)) : [])
                  .filter(p => !subprojectsSearch || (p.name?.toLowerCase().includes(subprojectsSearch.toLowerCase()) || p.subprojectNumber?.toLowerCase().includes(subprojectsSearch.toLowerCase())))
                  .map(sp => (
                    <div key={sp.id} className="dashboard-ticket-item" style={{ minWidth: 260, cursor: 'pointer' }} onClick={() => {
                      if (sp.project) {
                        const fullProject = projects.find(pr => pr.id === sp.project.id);
                        setShowProjectDetail({ mode: 'project', data: fullProject || sp.project });
                      } else {
                        setShowProjectDetail({ mode: 'subproject', data: sp });
                      }
                    }}>
                      <div className="dashboard-ticket-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span title={sp.name}>{sp.name}</span>
                        <span style={{ opacity: 0.7 }}>{sp.subprojectNumber}</span>
                      </div>
                      <div className="dashboard-ticket-meta" style={{ justifyContent: 'space-between' }}>
                        <div className="ticket-status-info">
                          <span className="ticket-status-text">{t('projects.completed', 'Abgeschlossen')} {sp.completedPercent || 0}%</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-light)' }}>{sp.ticketCount || 0} {t('projects.timeblocks', 'Zeitblöcke')}</div>
                      </div>
                    </div>
                ))}
              </div>
            </div>
          )}

          <div className="dashboard-section">
            <div className="section-header section-header-with-search">
              <Calendar size={20} />
              <h2>{t('projects.timeblocks', 'Zeitblöcke')}</h2>
              <div className="ticket-search-container">
                <div className="search-input-wrapper">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    placeholder={t('projects.searchTimeblocks', 'Zeitblöcke durchsuchen...')}
                    value={ticketBlocksSearch}
                    onChange={(e) => setTicketBlocksSearch(e.target.value)}
                    className="ticket-search-input"
                  />
                </div>
              </div>
            </div>
            {(() => {
              const filteredTickets = (() => {
                if (!selectedProjectId) return [];
                // Build set of subproject IDs for selected project
                const relatedSubIds = new Set(
                  subprojects.filter(sp => sp.project && sp.project.id === selectedProjectId).map(sp => sp.id)
                );
                return ticketBlocksFiltered.filter(t => (
                  (t.projectId && t.projectId === selectedProjectId) ||
                  (t.subprojectId && relatedSubIds.has(t.subprojectId))
                ));
              })();
              
              // Sortiere: abgeschlossene/archivierte ans Ende
              const sortedTickets = [...filteredTickets].sort((a, b) => {
                const aBottom = (a && (a.status === 'abgeschlossen' || a.isArchived));
                const bBottom = (b && (b.status === 'abgeschlossen' || b.isArchived));
                if (aBottom === bBottom) return 0;
                return aBottom ? 1 : -1;
              });
              
              if (!selectedProjectId || sortedTickets.length === 0) {
                return (
                  <div className="dashboard-ticket-list" style={{ display: 'flex', gap: 8, overflowX: 'auto' }} />
                );
              }

              return (
                <DashboardTicketList 
                  tickets={sortedTickets}
                  searchFilter={ticketBlocksSearch}
                  filterProjectLinked={false}
                  completedArchivedOpacity={0.2}
                  onTicketClick={(ticket) => {
                    setSelectedTicket(ticket);
                    setShowTicketDetail(true);
                  }}
                />
              );
            })()}
          </div>
        </div>
      </div>
      {showCreateProjectModal && (
        <CreateProjectWizard
          onClose={() => setShowCreateProjectModal(false)}
          onCreated={async (createdProject) => {
            try {
              const [projRes, subRes, templatesRes] = await Promise.all([
                apiService.getProjects({ status: 'active' }),
                apiService.getSubprojects({ status: 'active' }),
                apiService.getTemplates()
              ]);
              setProjects(projRes.projects || []);
              setSubprojects(subRes.subprojects || []);
              setTemplates(templatesRes.templates || []);
              const ticketsRes = await apiService.getTickets({ includeArchived: '1' });
              setTicketBlocks(ticketsRes.tickets || []);
              // Neu erstelltes oder geändertes Projekt direkt auswählen
              if (createdProject && createdProject.id != null) {
                setSelectedProjectId(createdProject.id);
                // Nach Erstellung Nachfrage: Direkt zur Zeitplanung wechseln?
                setPlanPromptProject(createdProject);
                setShowPlanPrompt(true);
              } else if (selectedProjectId) {
                // Falls ein Projekt bearbeitet wurde und keine ID zurückkommt, wähle das aktuell selektierte erneut
                setSelectedProjectId(selectedProjectId);
              }
            } catch (_) {}
            setShowCreateProjectModal(false);
          }}
        />
      )}
      {showTicketDetail && selectedTicket && (
        <TicketDetail 
          isMobileView={isMobileScreen}
          ticket={selectedTicket}
          onClose={async (hasChanges = false) => {
            setShowTicketDetail(false);
            const closedId = selectedTicket?.id;
            const candidateProjectId = (() => {
              try {
                if (selectedTicket?.projectId) return selectedTicket.projectId;
                if (selectedTicket?.subprojectId) {
                  const sp = (subprojects || []).find(s => Number(s.id) === Number(selectedTicket.subprojectId));
                  if (sp && sp.project && sp.project.id != null) return sp.project.id;
                }
                return null;
              } catch (_) { return null; }
            })();
            setSelectedTicket(null);

            try {
              if (hasChanges === 'completed') {
                // Entferne lokal sofort
                if (closedId) {
                  setTicketBlocks(prev => prev.filter(t => t.id !== closedId));
                }
                // Lade aktuelle Tickets (damit abgeschlossene/archivierte verschwinden)
                const res = await apiService.getTickets({ includeArchived: '1' });
                setTicketBlocks(res.tickets || []);
                dispatchTicketsUpdated('ticket_removed_or_completed');
                // Projekte/Unterprojekte/Vorlagen neu laden (Prozentzahlen, Zähler und Badge)
                try {
                  const [projRes, subRes, templatesRes] = await Promise.all([
                    apiService.getProjects({ status: 'active' }),
                    apiService.getSubprojects({ status: 'active' }),
                    apiService.getTemplates()
                  ]);
                  setProjects(projRes.projects || []);
                  setSubprojects(subRes.subprojects || []);
                  setTemplates(templatesRes.templates || []);
                  // Wenn Ticket abgeschlossen: serverseitig prüfen, ob Vorgang noch aktive Tickets hat
                  if (hasChanges === 'completed' && candidateProjectId) {
                    try {
                      const check = await apiService.hasActiveTicketsForProject(candidateProjectId);
                      if (check && check.success === true && check.hasActive === false) {
                        const proj = (projRes.projects || []).find(p => Number(p.id) === Number(candidateProjectId));
                        if (proj) {
                          
                        }
                      }
                    } catch (_) {}
                  }
                } catch (_) {}
              } else if (hasChanges === true || hasChanges === 'status_uncompleted') {
                const res = await apiService.getTickets({ includeArchived: '1' });
                setTicketBlocks(res.tickets || []);
                dispatchTicketsUpdated('ticket_updated');
                try {
                  const [projRes, subRes, templatesRes] = await Promise.all([
                    apiService.getProjects({ status: 'active' }),
                    apiService.getSubprojects({ status: 'active' }),
                    apiService.getTemplates()
                  ]);
                  setProjects(projRes.projects || []);
                  setSubprojects(subRes.subprojects || []);
                  setTemplates(templatesRes.templates || []);
                } catch (_) {}
              }
            } catch (_) {}
          }}
          onTicketUpdate={handleTicketUpdated}
        />
      )}
      {showProjectDetail && (
        <CreateProjectWizard
          onClose={() => setShowProjectDetail(false)}
          initialMode={showProjectDetail.mode}
          initialData={showProjectDetail.data}
          readOnly={false}
          onCreated={async () => {
            try {
              const [projRes, subRes, templatesRes] = await Promise.all([
                apiService.getProjects({ status: 'active' }),
                apiService.getSubprojects({ status: 'active' }),
                apiService.getTemplates()
              ]);
              setProjects(projRes.projects || []);
              setSubprojects(subRes.subprojects || []);
              setTemplates(templatesRes.templates || []);
              const ticketsRes = await apiService.getTickets({ includeArchived: '1' });
              setTicketBlocks(ticketsRes.tickets || []);
            } catch (_) {}
            setShowProjectDetail(false);
          }}
        />
      )}
      {showPlanPrompt && planPromptProject && (
        <Modal
          open
          onClose={() => setShowPlanPrompt(false)}
          title={t('projects.planPrompt.title', 'Zeitblöcke jetzt einplanen?')}
          maxWidth="520px"
          footer={<>
            <button type="button" className="btn btn-secondary" onClick={() => setShowPlanPrompt(false)}>{t('common.no', 'Nein')}</button>
            <button type="button" className="btn btn-primary" onClick={() => { const url = `/global-calendar?projectPlanId=${planPromptProject.id}&projectName=${encodeURIComponent(planPromptProject.name || '')}`; setShowPlanPrompt(false); navigate(url); }}>{t('projects.planPrompt.goToPlanning', 'Ja, zur Zeitplanung')}</button>
          </>}
        >
              <div style={{ padding: 8 }}>
                {t('projects.planPrompt.message', 'Möchten Sie die Zeitblöcke für "{{name}}" jetzt in der Kalenderansicht einplanen?', { name: planPromptProject.name || ('#' + planPromptProject.id) })}
              </div>
        </Modal>
      )}
    </div>
  );
};

export default ProjectsView;



